import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, ArrowRight, Crown, Loader2 } from 'lucide-react';
import { stripeService } from '../lib/stripe';
import { getProductByPriceId } from '../stripe-config';

const SuccessPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [subscription, setSubscription] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    const fetchSubscription = async () => {
      try {
        const subscriptionData = await stripeService.getUserSubscription();
        setSubscription(subscriptionData);
      } catch (error) {
        console.error('Error fetching subscription:', error);
      } finally {
        setLoading(false);
      }
    };

    // Add a small delay to ensure webhook has processed
    const timer = setTimeout(fetchSubscription, 2000);
    return () => clearTimeout(timer);
  }, []);

  const getProductInfo = () => {
    if (!subscription?.price_id) return null;
    return getProductByPriceId(subscription.price_id);
  };

  const product = getProductInfo();

  return (
    <div className="min-h-screen bg-gradient-to-br from-light-gray to-sky-blue/20 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-xl p-8 max-w-lg w-full text-center border border-white/50"
      >
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
          className="w-20 h-20 bg-gradient-to-r from-lime-green to-soft-teal rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg"
        >
          <CheckCircle className="w-10 h-10 text-white" />
        </motion.div>
        
        <h1 className="text-3xl font-bold text-charcoal mb-4">Payment Successful!</h1>
        
        {loading ? (
          <div className="flex items-center justify-center mb-6">
            <Loader2 className="w-6 h-6 animate-spin text-electric-blue mr-2" />
            <span className="text-slate-gray">Loading subscription details...</span>
          </div>
        ) : (
          <>
            {product && (
              <div className="bg-gradient-to-br from-electric-blue/10 to-deep-indigo/10 rounded-xl p-6 mb-6 border border-electric-blue/20">
                <div className="flex items-center justify-center mb-3">
                  <Crown className="w-6 h-6 text-electric-blue mr-2" />
                  <h2 className="text-xl font-semibold text-charcoal">{product.name}</h2>
                </div>
                <p className="text-slate-gray text-sm mb-3">{product.description}</p>
                {subscription && (
                  <div className="text-sm text-slate-gray">
                    <p><strong>Status:</strong> <span className="capitalize text-lime-green">{subscription.status}</span></p>
                    {subscription.current_period_end && (
                      <p><strong>Next billing:</strong> {new Date(subscription.current_period_end * 1000).toLocaleDateString()}</p>
                    )}
                  </div>
                )}
              </div>
            )}
            
            <p className="text-slate-gray mb-8">
              Welcome to Premium! You now have access to our exclusive CDMO network with advanced matching features, priority introductions, and premium support.
            </p>
          </>
        )}

        <div className="space-y-4">
          <Link to="/matchmaking">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="w-full px-6 py-3 bg-gradient-primary text-white font-semibold rounded-xl hover:shadow-lg transition-all duration-300 flex items-center justify-center"
            >
              Explore Premium Features
              <ArrowRight className="w-4 h-4 ml-2" />
            </motion.button>
          </Link>
          
          <Link to="/">
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="w-full px-6 py-3 border border-slate-gray/30 text-slate-gray font-medium rounded-xl hover:bg-light-gray transition-all duration-300"
            >
              Return Home
            </motion.button>
          </Link>
        </div>

        {/* {sessionId && (
          <div className="mt-6 p-3 bg-light-gray rounded-lg">
            <p className="text-xs text-slate-gray">
              <strong>Session ID:</strong> {sessionId}
            </p>
          </div>
        )} */}
      </motion.div>
    </div>
  );
};

export default SuccessPage;